import { matGroupAPI } from './urls';
import request from '../request';

// The group duplicate-check endpoint mirrors the school one: a BaseModel wrapper with a
// PascalCase data body. Normalise to camelCase, tolerating either casing per key.
const normaliseDuplicate = (response) => {
  const data = response?.data ?? response ?? {};
  return {
    isDuplicate: data.isDuplicate ?? data.IsDuplicate ?? false,
    matchedBy: data.matchedBy ?? data.MatchedBy ?? null,
    existingId: data.existingId ?? data.ExistingId ?? null,
    existingName: data.existingName ?? data.ExistingName ?? null,
  };
};

export const checkDuplicate = (name, groupUid, excludeId) => {
  return request({
    url: matGroupAPI.checkDuplicate(name, groupUid, excludeId),
    method: 'GET',
  })
    .then((response) => normaliseDuplicate(response))
    .catch((error) => error);
};

export const getAllMatGroups = () => {
  return request({
    url: matGroupAPI.getAllGroups,
    method: 'GET',
  })
    .then((response) => response)
    .catch((response) => response);
};

export const getAllMatGroupsPaginated = (pageNumber = 1, pageSize = 20, sortBy = 'Name', sortDirection = 'asc', searchTerm = '', includeOnlyTestAccounts = false) => {
  const params = new URLSearchParams({
    pageNumber: pageNumber.toString(),
    pageSize: pageSize.toString(),
    sortBy,
    sortDirection,
  });
  
  if (searchTerm) {
    params.append('searchTerm', searchTerm);
  }
  
  // Always send includeOnlyTestAccounts parameter explicitly
  params.append('includeOnlyTestAccounts', includeOnlyTestAccounts ? 'true' : 'false');
  
  return request({
    url: `${matGroupAPI.getAllMatGroupsPaginated}?${params.toString()}`,
    method: 'GET',
  })
    .then((response) => response)
    .catch((response) => response);
};

export const getMatGroupDetails = (id) => {
  return request({
    url: matGroupAPI.getMatGroupDetails(id),
    method: 'GET',
  })
    .then((response) => response)
    .catch((response) => response);
};

export const getMatGroup = (token = null, matCycleId = 0) => {
  token = token === null ? '' : token;
  return request({
    url: matGroupAPI.getMatGroup(token, matCycleId),
    method: 'GET',
  })
    .then((response) => response)
    .catch((response) => response);
};

export const createMatSurvey = (data) => {
  return request({
    url: matGroupAPI.createMatSurvey,
    method: 'POST',
    data: data,
  })
    .then((response) => response)
    .catch((error) => error);
};

export const closeMatSurvey = (data) => {
  return request({
    url: matGroupAPI.closeMatSurvey,
    method: 'POST',
    data: data,
  })
    .then((response) => response)
    .catch((error) => error);
};

export const addMatGroup = (model) => {
  return request({
    url: matGroupAPI.addMatGroup,
    method: 'POST',
    data: model,
  })
    .then((response) => response)
    .catch((response) => response);
};

export const updateMatGroup = (model) => {
  return request({
    url: matGroupAPI.updateMatGroup,
    method: 'PUT',
    data: model,
  })
    .then((response) => response)
    .catch((response) => response);
};

export const UpdateMatSchoolSubscriptions = (model) => {  
  return request({
    url: matGroupAPI.UpdateMatSchoolSubscriptions,
    method: 'POST',
    data: model,
  })
    .then((response) => response)
    .catch((response) => response);
};


export const deleteMatGroup = (id) => {
  // DO SOMETHING
};

export const getGroupHeatMap = (id) => {
  return request({
    url: matGroupAPI.getGroupHeatMap(id),
    method: 'GET',
  })
    .then((response) => response)
    .catch((response) => response);
};

export const getMatById = (id) => {
  return request({
    url: matGroupAPI.getMatById(id),
    method: 'GET',
  })
    .then((response) => response)
    .catch((response) => response);
};

export const setCanCreateSurvey = (model) => {
  return request({
    url: matGroupAPI.setCanCreateSurvey,
    method: 'POST',
    data: model,
  })
    .then((response) => response)
    .catch((response) => response);
};

export const getGroupPresentationReport = (
  token,
  matGroupId,
  matCycleId,
  IsSurveyClosed
) => {
  return request({
    url: matGroupAPI.getGroupPresentation(
      token,
      matGroupId,
      matCycleId,
      IsSurveyClosed
    ),
    method: 'GET',
  })
    .then((response) => response)
    .catch((response) => response);
};

export const getMatGroupResults = (token, matGroupId, matCycleId) => {
  return request({
    url: matGroupAPI.getMatGroupResults(token, matGroupId, matCycleId),
    method: 'GET',
  })
    .then((response) => response)
    .catch((response) => response);
};

export const getMatGroupResultsByCategoryId = (
  categoryId,
  matGroupId,
  matCycleId
) => {
  return request({
    url: matGroupAPI.getMatGroupResultsByCategoryId(
      categoryId,
      matGroupId,
      matCycleId
    ),
    method: 'GET',
  })
    .then((response) => response)
    .catch((response) => response);
};

const getMatSchools = (groupId = 0) => {
  return request({
    url: matGroupAPI.getMatSchools(groupId),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};

const getMatSchoolsWithOpenSurveyForCycle = (groupId = 0) => {
  return request({
    url: matGroupAPI.getMatSchoolsWithOpenSurveyForCycle(groupId),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};

const removeSchoolFromGroup = (schoolId, groupId) => {
  return request({
    url: matGroupAPI.removeSchoolFromGroup(schoolId, groupId),
    method: 'PUT',
  })
    .then((response) => response)
    .catch((error) => error);
};

const addSchoolInGroup = (schoolId, groupId) => {
  return request({
    url: matGroupAPI.addSchoolInGroup(schoolId, groupId),
    method: 'PUT',
  })
    .then((response) => response)
    .catch((error) => error);
};

const updateMatUsers = (model) => {
  return request({
    url: matGroupAPI.updateMatUsers,
    method: 'PUT',
    data: model,
  })
    .then((response) => response)
    .catch((error) => error);
};

const archiveGroup = (groupId) => {
  return request({
    url: matGroupAPI.archiveGroup(groupId),
    method: 'DELETE',
  })
    .then((response) => response)
    .catch((error) => error);
};

// Restores only the group itself; its schools stay archived and are unarchived one at a time.
const unarchiveGroup = (groupId) => {
  return request({
    url: matGroupAPI.unarchiveGroup(groupId),
    method: 'POST',
  })
    .then((response) => response)
    .catch((error) => error);
};

export const getMatGroupLogo = (token, matId) => {
  return request({
    url: matGroupAPI.getMatGroupLogo(token, matId),
    method: 'GET',
  })
    .then((response) => response)
    .catch((response) => response);
};
export const getMatGroupReport = (matId, matCycleId) => {
  return request({
    url: matGroupAPI.GetMatReport(matId, matCycleId),
    method: 'GET',
  })
    .then((response) => response)
    .catch((response) => response);
};

export const GetMatReportPdf = (matId, matCycleId) => {
  return request({
    url: matGroupAPI.GetMatReportPdf(matId, matCycleId),
    method: 'POST',
    responseType: 'blob',
  })
    .then((response) => response)
    .catch((response) => response);
};

export const getMatDetailByUserContext = (matId, matCycleId) => {
  return request({
    url: matGroupAPI.getMatDetailByUserContext(matId, matCycleId),
    method: 'GET'
  })
    .then((response) => response)
    .catch((response) => response);
};

const matGroupService = {
  getMatGroupLogo,
  getAllMatGroups,
  getAllMatGroupsPaginated,
  getMatGroupDetails,
  getMatGroup,
  createMatSurvey,
  closeMatSurvey,
  addMatGroup,
  checkDuplicate,
  updateMatGroup,
  UpdateMatSchoolSubscriptions,
  deleteMatGroup,
  getGroupHeatMap,
  getMatById,
  setCanCreateSurvey,
  getGroupPresentationReport,
  getMatGroupResults,
  getMatGroupResultsByCategoryId,
  getMatSchools,
  removeSchoolFromGroup,
  addSchoolInGroup,
  updateMatUsers,
  archiveGroup,
  unarchiveGroup,
  getMatGroupReport,
  GetMatReportPdf,
  getMatSchoolsWithOpenSurveyForCycle,
  getMatDetailByUserContext,
};

export default matGroupService;
